import React from 'react';

import cx from 'classnames';

import { FocusIndicator } from '../FocusIndicator/FocusIndicator';

const variantClassMap = {
  disabled: 'text-disabled bg-grey-50',
  default: {
    button: 'text-cyan-700 bg-cyan-50 hover:bg-cyan-100 focus:bg-cyan-100',
    focus: 'text-cyan-700',
  },
  risky: {
    button:
      'text-orange-800 bg-orange-50 hover:bg-orange-100 focus:bg-orange-100',
    focus: 'text-orange-800',
  },
};

interface PillButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  /** Colour style variants. */
  appearance?: 'default' | 'risky';
  /** Adjusts spacing when using an icon as the only child. */
  iconOnly?: boolean;
  DEBUG_HOVER?: boolean;
}

export const PillButton = React.forwardRef<
  HTMLButtonElement | null,
  PillButtonProps
>(
  (
    {
      children,
      appearance = 'default',
      iconOnly,
      DEBUG_HOVER,
      className,
      ...rest
    },
    ref
  ) => {
    const buttonClass = React.useMemo(
      () =>
        cx(
          'group relative m-0',
          iconOnly ? 'w-16 h-16 p-4' : 'px-6 py-4 md:px-10 md:py-6',
          'font-display text-display font-600 leading-8 antialiased',
          'border-0 rounded-pill appearance-none outline-none select-none',
          'transition ease-in-out duration-150',
          rest.disabled
            ? variantClassMap.disabled
            : ['cursor-pointer', variantClassMap[appearance].button],
          __DEV__ && {
            'bg-cyan-100': DEBUG_HOVER && appearance === 'default',
            'bg-orange-100': DEBUG_HOVER && appearance === 'risky',
          },
          { [className]: !!className }
        ),
      [rest.disabled, appearance, iconOnly, DEBUG_HOVER, className]
    );
    const focusClass = variantClassMap[appearance].focus;

    return (
      <button ref={ref} className={buttonClass} type="button" {...rest}>
        <FocusIndicator colorClass={focusClass} />
        <div className="relative flex items-center justify-center">
          {children}
        </div>
      </button>
    );
  }
);
PillButton.displayName = 'PillButton2';
